require('./pannellum-controls-basic.less');
require('./template/floorplans/style.scss');
const debounce = require('lodash/debounce');

import panzoom from 'panzoom';
import MakeFloorplanScreenshots from '../lib/service/makeFloorplanScreenshots';
import E3ButtonTooltip from '../lib/e3-button-tooltip/e3-button-tooltip';
import E3TourInfo from '../lib/e3-tour-info/e3-tour-info';
import E3TourHelp from '../lib/e3-tour-help/e3-tour-help';
import E3MenuDrawer from '../lib/e3-menu-drawer';
import HelpService from '../lib/service/help.service';
import {E3Select} from '../lib/ui/';
import {isMetrical, changeMeasuringSystem} from '../lib/service/geo.service';
import {sortByAltitude} from '../lib/helpers/common';
import getCookie from "lib/cookie/getCookie";
import {isCustomFloorplan} from 'a_common-folder/services/floorplans.service';
import { canShareScreen } from 'a_root-folder/lib/service/webrtc.service';

const onBeforeEvent = 'before.event.trigger';
const detectBrowser = require('a_common-folder/utils/detectBrowser');
const DetectRTC = require('detectrtc');

export default class PannellumPanel {
    static get POSITION() {
        return {
            RIGHT: 'right',
            LEFT: 'left',
            CENTER: 'center',
            CONTAINER: 'container'
        };
    }

    /**
     * Controls that stay in the bottom bar. Everything else added to
     * POSITION.CONTAINER goes into the menu drawer (viewer only — the admin
     * editor keeps the flat bar).
     */
    static get BAR_CONTROLS() {
        return [
            'menu',
            'scenes',
            'floorplans_2d',
            'floorplans_3d',
            'model3d',          // 3D walkthrough (lib/convert3d/public.js), only when a model is built
            'marble3d',         // 3D Marble AI world (lib/convert3d/public.js), only when worlds were generated
            'language',
            // Live-session controls are transient and time-critical: keep them
            // one tap away for as long as a call is running.
            'e3livePlus',
            'e3liveClose',
            'e3liveScreenShare'
        ];
    }

    constructor(container, editable, title, e3liveRoomId, disableE3live, tourInfoAvailable, translationService, {isShowLanguage, countryCode, permissions, narration_json={}, scenes}) {
        this._pannellum = container;
        this._classPrefix = 'pannellum-panel';
        this._permissions = permissions;
        this._controlButtons = {};
        this._events = {};
        this._panelMode = null;
        this._disableE3live = disableE3live;
        this._editable = editable;
        this._tourInfoAvailable = tourInfoAvailable;
        this._e3liveRoomId = e3liveRoomId;
        this._translationService = translationService;
        this._translationPromise = translationService.getTranslation();
        this._translate = translationService.translate.bind(translationService);
        this._contentData = {};
        this._preview = {};
        this._eventActions = [];
        this._helperService = new HelpService();
        this._urlParams = this._helperService.urlParams;
        this._hidefullscreen = this._urlParams['hidefullscreen'] || false;
        this.share_button = this._urlParams['share_button']  === 'false' ? false : true;
        this._hideLeadGeneration = this._urlParams['lead_gen']  === 'true' ? false  : true;
        this._measuring_tool = this._urlParams['measuring_tool'];
        this._accessibility = this._urlParams['accessibility'] === 'false' ? false : true;
        this.e3plan = {};
        this._floorplan = {
            state: '2d',
            button: {}
        };
        this._title = {
            tour: '',
            scene: ''
        };
        this._sceneId = null;
        this.floorplanPromise = {};
        this._showFloorplanID = null;

        this._tooltip = new E3ButtonTooltip(translationService, {className: 'e3-button-tooltip'});

        this._touchDeviceCallback(window.e3touchDevice);

        this._initPanel();

        this._initContent(title);
        this.metrical = true;
        this._currentScreenshot = null;
        this._isOculusBrowser = detectBrowser.isOculusBrowser;
        this.onMoveToScene = true;
        this._narrationScenes = this.flattenNarration(narration_json);
        this._scenes = scenes;
        this._countryCode = countryCode;
        this._isShowLanguage = isShowLanguage;

        window.e3touchDeviceCallback = this._touchDeviceCallback.bind(this);
    }

    flattenNarration(narrationJSON) {
        if(!narrationJSON) return [];
        
        const json = [];
        for(var i in narrationJSON.narration){ 
            json.push(narrationJSON.narration[i].scenes);
        }

        return [...new Set(json.flat())];
    }

    currentMode() {
        return this._panelMode;
    }

    _removeActionFloorplanListener() {
        this._eventActions.forEach((actions) => {
            this.off(actions[0], actions[1])
        })
    }

    initControls() {
        this._controls = {
            container: document.createElement('div'),
            left: document.createElement('div'),
        };
        this._setClass(this._controls.container, 'controls');
        this._panel.appendChild(this._controls.container);

        this._setClass(this._controls.left, 'left');
        this._setClass(this._controls.left, 'controls');
        this._panel.appendChild(this._controls.left);

        this._initMenuDrawer();
    }

    _initMenuDrawer() {
        if (this._editable) return;

        const menuButton = () => this._controlButtons['menu'] && this._controlButtons['menu'].button;

        this._drawer = new E3MenuDrawer(this._pannellum, {
            translationService: this._translationService,
            anchor: 'left',
            onOpen: () => {
                const button = menuButton();
                if (button) button.classList.add('active');
            },
            onClose: () => {
                const button = menuButton();
                if (button) button.classList.remove('active');
            }
        });

        // index -1 puts it ahead of `scenes` (0), the first bar control.
        this._addSimpleControl('menu', 'menu', PannellumPanel.POSITION.CONTAINER, -1, false, false, 'TOOLTIP_MENU', null, 'left');
        this.on('menu', () => this._drawer.toggle());

        this._updateMenuButton();
    }

    /**
     * The drawer trigger is pointless while nothing lives in the drawer — every
     * optional control can be switched off by url params or tour settings.
     */
    _updateMenuButton() {
        const button = this._controlButtons['menu'] && this._controlButtons['menu'].button;
        if (!this._drawer || !button) return;

        const empty = this._drawer.isEmpty();

        button.classList[empty ? 'add' : 'remove']('hidden');
        if (empty && this._drawer.isOpen) this._drawer.close();
    }

    addBasicControl(name, position, index, title, dataset) {
        this._addSimpleControl(name, name, position, index, false, false, title, dataset);
    }

    updateE3Plan(e3plan) {
        this.e3plan = e3plan;
    }

    addVRButton() {
        this.addBasicControl('vr', PannellumPanel.POSITION.CONTAINER, 3, 'TOOLTIP_VR');
    }

    addBasicControls() {
        let close = function () {
            if (this._panelMode == 'floorplans_2d') {
                this._closePanelFull();
            }
        }.bind(this);

        if (!this._isOculusBrowser && !(this._inIframe() && this._isIOS()) && this._webglSupported() && !this._e3liveRoomId && this._isMobile()) {
            this.addVRButton();
        }

        if (!this._e3liveRoomId && !this._editable && this._disableE3live !== 'true') {
            this.addBasicControl('e3live', PannellumPanel.POSITION.CONTAINER, 7, 'TOOLTIP_E3LIVE');
        }

        if (this._e3liveRoomId && !this._editable) {
            this.addBasicControl('e3livePlus', PannellumPanel.POSITION.CONTAINER, 8, 'TOOLTIP_E3LIVE_PLUS');
            this.addBasicControl('e3liveClose', PannellumPanel.POSITION.CONTAINER, 9, 'TOOLTIP_E3LIVE_CANCEL');
            if(canShareScreen()){
                this.addBasicControl('e3liveScreenShare', PannellumPanel.POSITION.CONTAINER, 9, 'TOOLTIP_E3LIVE_SCREEN_SHARE');
            }
        }

        if(!this._editable && !this._hideLeadGeneration && !getCookie('hideLeadGen')){
            this.addBasicControl('leadGen', PannellumPanel.POSITION.CONTAINER, 9, 'TOOLTIP_LEAD_GEN');
        }

        if (this._editable || this._tourInfoAvailable) {
            this.addTourInfo();
        }

        this.addTourHelp();

        if (!this._editable && !this._e3liveRoomId && this.share_button) {
            this.addBasicControl('share', PannellumPanel.POSITION.CONTAINER, 100, 'TOOLTIP_SHARE');
        }

        if (!this._hidefullscreen) {
            this.addBasicControl('fullscreen', PannellumPanel.POSITION.CONTAINER, 199, 'TOOLTIP_FULLSCREEN');
        }

        if (this.e3plan && this._measuring_tool) {
            this.addBasicControl('measurement', PannellumPanel.POSITION.CONTAINER, 6, 'TOOLTIP_MEASUREMENT');
        }

        if (this._accessibility) {
            this.addBasicControl('accessibility', PannellumPanel.POSITION.CONTAINER, 6, 'TOOLTIP_ACCESSIBILITY');
        }

        if (!this._editable && this._isShowLanguage.length) {
            this.addBasicControl('language', PannellumPanel.POSITION.CONTAINER, 201, '', {value: this._countryCode});
        }

        this._triggerEvent('basic-conrolls-complete')
    }

    removeMuteBackgroundButton(){
        this._removeSimpleControl('volume--mute');
    }

    removePlayBackgroundButton(){
        this._removeSimpleControl('volume--play');
    }

    addMuteBackgroundButton(){
        this.addBasicControl('volume--mute', PannellumPanel.POSITION.LEFT, 198, 'TOOLTIP_BACKGROUND_MUTE');
    }

    addPlayBackgroundButton(){
        this.addBasicControl('volume--play', PannellumPanel.POSITION.LEFT, 197, 'TOOLTIP_BACKGROUND_MUTE');
    }

    isFloorplansButton() {
        return this._controlButtons['floorplans_2d'] && this._controlButtons['floorplans_3d'];
    }

    addFloorPlan(markers, floors, floorplanButton, floorplanHide) {
        this.floors = floors;
        this.markers = markers;
        this.floorplanHide = floorplanHide || (() => true);

        this.addRasterFloorplan();
        this.add3dFloorplan(floorplanButton);
    }

    hidePanel() {
        this._contentData[this._panelMode] && this._openContent(this._panelMode);
    }


    add3dFloorplan(floorplanButton) {
        if (!this.e3plan) return;

        if (floorplanButton && !this._controlButtons['floorplans_3d']) {
            this._addSimpleControl(
                'floorplans_3d',
                'floorplans_3d',
                PannellumPanel.POSITION.CONTAINER,
                5, false, false,
                'TOOLTIP_3D_FLOORPLAN'
            );
        }
    }

    _toogleSelectFloor() {
        const popup = this._content.querySelector('#menu .selection');
        if (popup) popup.classList.toggle('hide');
    }

    _changeMenuTitle(text) {
        const menuTitle = document.querySelector('.menu-title');
        if (menuTitle) menuTitle.innerText = text;
    }

    addTourInfo() {
        this._addSimpleControl('e3-tour-info', 'e3-tour-info', PannellumPanel.POSITION.CONTAINER, 10, true, false, 'TOOLTIP_E3TOUR_INFO');

        this._contentData['e3-tour-info'] = {
            init: this._initTourInfo.bind(this),
            destroy: this._destroyTourInfo.bind(this)
        }
    }

    _initTourInfo() {
        this._tourInfo = new E3TourInfo(this._content, {editor: this._editable}, this._translationService);

        this._content.classList.add('loading');

        this._setClass(this._panel, '-tour-info');

        let promise = new Promise((resolve, reject) => this._triggerEvent('e3-tour-info', {resolve, reject}));

        promise
            .then((body) => this._tourInfo.show(body))
            .catch((error) => console.error(error))
            .finally(() => this._content.classList.remove('loading'));

        this._tourInfo.on('tour-info.cancel', this._closeTourInfo.bind(this));

        if (!this._editable) return;

        this._tourInfo.on('tour-info.save', this._onTourInfoSave.bind(this));
        this._tourInfo.on('tour-info.delete', this._onTourInfoDelete.bind(this));

        this._tourInfo.on('ql-street-open', this._onTourOpenGmapStreet.bind(this));
    }


    addTourHelp() {
        this._addSimpleControl('e3-tour-help', 'e3-tour-help', PannellumPanel.POSITION.CONTAINER, 200, true, false, 'TOOLTIP_E3TOUR_HELP');

        this._contentData['e3-tour-help'] = {
            init: this._initTourHelp.bind(this),
            destroy: this._destroyTourHelp.bind(this)
        }
    }

    _initTourHelp() {
        this._tourHelp = new E3TourHelp(this._content, {editor: this._editable}, this._translationService);

        this._setClass(this._panel, '-tour-help');

        this._tourHelp.appendIframe();
        this._tourHelp.on('tour-help.cancel', this._closeTourHelp.bind(this));
        this._tourHelp.on('tour-help.cancel', () => this._triggerEvent('e3-tour-help.close'));

        if (!this._editable) return;
    }


    _destroyTourHelp() {
        let promise = Promise.resolve();

        this._content.classList.add('loading');

        promise.then(() => {
            this._tourHelp.off('tour-help.cancel');

            if (!this._panel) return;

            this._removeClass(this._panel, '-tour-help');
            this._tourHelp.destroy();
        }).finally(() => {
            if (this._content) this._content.classList.remove('loading')
        });

        return promise;
    }

    _closeTourHelp() {
        this._destroyTourHelp();
        this._closePanel();
        this._resetControlsButtonsClassNames();

        this._panelMode = null;
    }

    _onTourOpenGmapStreet(editor) {
        this._triggerEvent('ql-street-open', editor);
    }
    _onTourInfoSave({html, resolve, reject}) {
        this._triggerEvent('tour-info.save', {html, resolve, reject});
    }

    _onTourInfoDelete({resolve, reject}) {
        this._triggerEvent('tour-info.delete', {resolve, reject});
    }

    _destroyTourInfo() {
        let promise = Promise.resolve();

        if (this._editable) {
            this._tourInfo.off('tour-info.save');
            this._tourInfo.off('tour-info.delete');

            if (this._tourInfo.isDirty())
                promise = new Promise((resolve, reject) => {
                    this._onTourInfoSave({html: this._tourInfo.getContent(), resolve, reject})
                })
        }

        this._content.classList.add('loading');

        promise.then(() => {
            this._tourInfo.off('tour-info.cancel');

            if (!this._panel) return;

            this._removeClass(this._panel, '-tour-info');
            this._tourInfo.destroy();
        }).finally(() => {
            if (this._content) this._content.classList.remove('loading')
        });

        return promise;
    }

    _closeTourInfo() {
        this._destroyTourInfo();
        this._closePanel();
        this._resetControlsButtonsClassNames();

        this._panelMode = null;
    }

    saveTourInfo() {
        let promise = Promise.resolve();

        if (this._panelMode !== 'e3-tour-info' || !this._tourInfo || !this._tourInfo.isDirty()) return promise;

        promise = new Promise((resolve, reject) => {
            this._onTourInfoSave({html: this._tourInfo.getContent(), resolve, reject})
        });

        return promise;
    }

    _resetControlsButtonsClassNames() {
        this._eachControlButton((button) => button.classList.remove('active'));
    }

    resetControlsButtonsActiveState() {
        this._eachControlButton((button) => button.classList.remove('active'));
    }

    // Walks every registered control — bar groups and drawer alike — instead of
    // querying a single container.
    _eachControlButton(callback) {
        for (let name in this._controlButtons) {
            const control = this._controlButtons[name];
            if (control && control.button) callback(control.button, name);
        }
    }

    addRasterFloorplan() {
        if (!this._controlButtons['floorplans_2d']) this._addSimpleControl('floorplans_2d', 'floorplans_2d', PannellumPanel.POSITION.CONTAINER, 4, true, false, 'TOOLTIP_2D_FLOORPLAN');

        this._contentData.floorplans_2d = {
            init: this._2dfloorplanInit.bind(this, this.floors, this.markers),
            destroy: this._2dfloorplansContentDestroy.bind(this),
            onfloorselect: function (e) {
                this.floorplanHide();
                this._selectRasterPlan(e.currentTarget, this.markers
                    .filter((m) => m.floorplanID === e.target.dataset.floorId)
                );
                this._changeMenuTitle(e.currentTarget.innerText);
                this._toogleSelectFloor();
            }.bind(this),
            onfloorimageselect: function (e) {
                if(!this.onMoveToScene) return ;

                this._onFloorImageSelect(e.currentTarget.parentElement);
            }.bind(this),
            resize: function () {
                if(!this._content) return ;

                var img = this._content.querySelector('.' + this._classPrefix + '-content-floorplans_2d-content .' + this._classPrefix + '-content-floorplans_2d-plan');
                var imgRatio = parseFloat(img.dataset.aspect);
                var el = img.parentNode,
                    parent = el.parentNode,
                    height = el.parentNode.parentNode.parentNode.parentNode.parentNode.offsetHeight - 110 + 'px';
                parent.style.height = parent.style.minHeight = parent.style.maxHeight = height;
                var containerRatio = parent.offsetWidth / parent.offsetHeight;
                if (containerRatio < imgRatio) {
                    el.style.width = parent.offsetWidth + 'px';
                    el.style.height = parent.offsetWidth / imgRatio + 'px';
                } else {
                    el.style.height = parent.offsetHeight + 'px';
                    el.style.width = parent.offsetHeight * imgRatio + 'px';
                }
            }.bind(this),
            listener: {
                'content': () => this._openContent('floorplans_2d',  this._controlButtons.floorplans_2d.button),
                'object': (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };
        this._updateImageData();
    }

    init2DFloorplan(){
        return  this._contentData['floorplans_2d'].init(this.floors, this.markers);
    }

    _2dfloorplanInit(floors, markers) {
        this._floorplan.state = '2d';
    
        if (!floors || !floors.length) {
            this._floorplansLoader();
            let promises = [];
            this.makeFloorplanScreenshots = new MakeFloorplanScreenshots(this._editable);
            promises.push(this.makeFloorplanScreenshots.loadScreen(this.e3plan));
            promises.push(isMetrical());

            Promise.all(promises).then(([data, metrical]) => {
                this._2dfloorplansContentDestroy();
                this.metrical = metrical;
                if (this._panelMode !== 'floorplans_2d') return;

                let floors = [];
                let markers = data.markers;
                let screenShots = data.screenshots;
                screenShots.map((res) => {
                    floors.push({
                        title: this.e3plan.layers[res.id].name,
                        id: res.id,
                        image_url: res.src,
                        image_url_imperial: res.image_url_imperial,
                        without_measurement: res.without_measurement,
                        altitude: this.e3plan.layers[res.id].altitude
                    })
                });

                this.floors = floors;
                this.markers = markers;

                this._rasterFloorPlansContentInit(floors, markers, {metrical: this.metrical});
            })
        } else {
            isMetrical().then((metrical) => {
                this.metrical = metrical;
                this._rasterFloorPlansContentInit(floors, markers, {metrical: this.metrical});
            })
        }
    }

    _initFloorSelect(container, floors, value, classes = []) {
        const singleFloor = floors.length === 1;
        floors = sortByAltitude(floors);
        const floorsList = E3Select.convertToSelectArray(floors, 'title', 'id');
        const floorSelect = new E3Select(container, {
            hidden: singleFloor,
            className: ['e3plan-floor-select', 'e3plan-floor-select-2d', ...classes],
            initValue: value,
            'mouseover': this._preventDefault,
            'mouseenter': this._preventDefault,
            'mouseleave': this._preventDefault,
        }, floorsList);

        return floorSelect;
    }

    _preventDefault(e){
        e.preventDefault();
        e.stopPropagation();
    }

    _rasterFloorPlansContentInit(floors, markers, props) {
        let {metrical} = props;
        let table = document.createElement('div');
        table.classList.add('table');
        let selectContainer = document.createElement('div');
        selectContainer.classList.add('select-container');
        if(DetectRTC.isMobileDevice){
            selectContainer.classList.add('select-container-mobile');
        }

        let selectContainerBody = document.createElement('div');
        selectContainerBody.classList.add('select-container-body');

        selectContainer.appendChild(selectContainerBody);
        table.appendChild(selectContainer);
    
        let body = document.createElement('div');
        body.classList.add('tr');

        let td = document.createElement('div');
        td.classList.add('td');

        let content = document.createElement('div');
        let th, button, active;

        this._setClass(this._panel, '-floorplans_2d');
        this._setClass(table, 'content-floorplans_2d');

        td.colSpan = floors.length;
        td.style.textAlign = 'center';

        this._setClass(content, 'content-floorplans_2d-content');

        window.addEventListener('resize', this._contentData.floorplans_2d.resize);

        let activeFloorplan = this._findActiveFloorplan(markers, this._sceneId);

        activeFloorplan = activeFloorplan ? activeFloorplan : floors[0].id;
        this._floor2DSelect = this._initFloorSelect(selectContainerBody, floors, activeFloorplan);

        this._floor2DSelect.on('set.value', (e) => this._selectRasterPlan(e.value, markers));

        if (floors[0].image_url_imperial) {
            let systems = [{title: 'm', id: 0}, {title: 'ft', id: 1}]
            let activeSystems = metrical ? 0 : 1;
            let classes = ['e3plan-system-select'];
            if(this._permissions['dimensions'] === false){
                classes.push('hidden');
            }
            this._system2DSelect = this._initFloorSelect(selectContainerBody, systems, activeSystems, classes);
            this._system2DSelect.on('set.value', (e) => this._changeMetricalSystem(e.value, markers));
        }

        if(!isCustomFloorplan(this.floors)){
            this._addDisclaimer(selectContainer);
        }
        td.appendChild(content);
        table.appendChild(body);
        body.appendChild(td);
        this._content.appendChild(table);
        this._contentData.floorplans_2d.content = content;

        this._selectRasterPlan(activeFloorplan, markers);

        const objectEl = !floors[0].image_url_imperial ? this._content.querySelector('img') : this._content.querySelector('object');
        objectEl.parentElement.addEventListener("click", this._contentData.floorplans_2d.listener.object);
        this._content.addEventListener("click", this._contentData.floorplans_2d.listener.content);
        
        active = null;
        table = null;
        // head = null;
        th = null;
        td = null;
        button = null;
        body = null;
        content = null;
    }

    hide(arrEl) {
        arrEl.forEach((el) => {
            this._controlButtons[el].button.classList.add('hidden');
        })

        this._updateMenuButton();
    }

    show(arrEl) {
        arrEl.forEach((el) => {
            this._controlButtons[el].button.classList.remove('hidden');
        })

        this._updateMenuButton();
    }

    _onFloorImageSelect(imageElem) {
        this._triggerEvent('scenes.select', imageElem.dataset.sceneId);
        this._controlButtons.floorplans_2d.button.click();
    }

    _changeMetricalSystem(value, markers) {
        this.metrical = !value ? true : false;
        changeMeasuringSystem(this.metrical);
        this._selectRasterPlan(this._currentScreenshot, markers)
    }

    _selectRasterPlan(value, markers) {
        this._currentScreenshot = value;
        markers = markers.filter((marker) => marker.floorplanID === value);

        if (this._contentData.floorplans_2d.content) {
            this._destroyFloorContent();
        }
        const container = this._contentData.floorplans_2d.content;
        const floorplan = this._contentData.floorplans_2d.images[value];
        let photoSpot, i;

        container.appendChild(floorplan);

        const activeScene = this._sceneId;

        for (i = 0; i < markers.length; i++) {
            photoSpot = document.createElement('span');
            photoSpot.style.left = markers[i].x + '%';
            photoSpot.style.top = markers[i].y + '%';
            photoSpot.dataset.sceneId = markers[i].imageID;
            if (activeScene && activeScene === markers[i].imageID) {
                photoSpot.classList.add('active');
            }

            const photoSpotClickZone = document.createElement('span');
            photoSpot.appendChild(photoSpotClickZone);
            this._setClass(photoSpotClickZone, 'content-floorplans_2d-spot__clickablezone');

            photoSpotClickZone.addEventListener('mouseup', this._contentData.floorplans_2d.onfloorimageselect);
            photoSpotClickZone.addEventListener('touchend', this._contentData.floorplans_2d.onfloorimageselect);
            this._setClass(photoSpot, 'content-floorplans_2d-spot');

            container.appendChild(photoSpot);
        }
        this._contentData.floorplans_2d.resize();
        this._runZoom();

        photoSpot = null;
    }

    _runZoom() {
        let parentElement = this._contentData.floorplans_2d.content.parentElement;
        const instance = panzoom(parentElement, {
            minZoom: 0.5,
            bounds: true,
            boundsPadding: 0.3
        });

        const spots = parentElement.querySelectorAll('.pannellum-panel-content-floorplans_2d-spot');

        instance.on('transform', (e) => {
            const {scale} = e.getTransform();
            spots
                .forEach((el) => {
                    el.style.transform = `scale(${1/scale}) translate(-50%, -50%)`
                })
        });

        this.delayTimeout = null;
        this.moveScene = (move) => {
            this.onMoveToScene = move;
            this.clearMoveScene();
        };
        this.clearMoveScene =  debounce(() => {
            this.onMoveToScene = true;
        }, 100)


        instance.on('panstart', (e) => this.onMoveToScene = false);

        instance.on('panend', (e) => this.moveScene(false));

        instance.on('zoom', (e) => this.moveScene(false));

        instance.on('zoomend', (e) => this.moveScene(false));

        instance.on('panzoomchange', (e) => this.moveScene(false));

    }

    _destroyFloorContent() {
        var container = this._contentData.floorplans_2d.content;
        var spots = container.getElementsByClassName(this._classPrefix + 'content-floorplans_2d-spot');
        var i;
        for (i = 0; i < spots.length; i++) {
            spots[i].removeEventListener('click', this._contentData.floorplans_2d.onfloorimageselect);
        }
        this._removeChildren(container);
    }

    _2dfloorplansContentDestroy() {
        const container = this._content;

        if (this._floor2DSelect) {
            this._floor2DSelect.destroy();
            this._floor2DSelect = null;
        }

        window.removeEventListener('resize', this._contentData.floorplans_2d.resize);

        const content2dPlan = this._content.querySelector('object');
       
        if(content2dPlan){
            content2dPlan.parentElement.removeEventListener("click", this._contentData.floorplans_2d.listener.object);
        }
        this._content.removeEventListener("click", this._contentData.floorplans_2d.listener.content);

        this._removeClass(this._panel, '-floorplans_2d');
        this._removeChildren(container);
    }

    _floorplansLoader() {
        let block = document.createElement('floorplans_2d');
        block.classList.add('loading')
        this._setClass(this._panel, '-floorplans_2d');
        this._setClass(block, 'content-floorplans_2d');

        this._content.appendChild(block);
    }

    _addDisclaimer(head) {
        const disclaimer = document.createElement('div');
        disclaimer.classList.add('floorplan-icon-disclaimer');
        disclaimer.classList.add('e3-select');
        disclaimer.classList.add('e3plan-floor-select');
        disclaimer.classList.add('e3plan-floor-select-2d');

        if(DetectRTC.isMobileDevice){
            disclaimer.classList.add('floorplan-icon-disclaimer-mobile');
        }

        this._translationPromise.then(() => {
            disclaimer.innerText = this._translate('DISCLAIMER');
        });
        head.appendChild(disclaimer)
    }

    _addCloseButton(head) {
        const closeButton = document.createElement('div');
        closeButton.classList.add('floorplan-icon-close');
        closeButton.onclick = this._openContent.bind(this, this._panelMode);
        head.appendChild(closeButton)
    }

    _createImageObj() {
        let imgEl = new Image();

        if(!isCustomFloorplan(this.floors)) {
            imgEl = document.createElement('object');
            imgEl.type = "image/svg+xml";

            Object.defineProperty(imgEl, "src", {
                set(src) {
                    imgEl.data = src;
                },
            });
        }

        return imgEl;
    }

    _updateImageData() {
        Object.defineProperty(this._contentData.floorplans_2d, 'images', {
            get: () => {
                return (this._contentData.floorplans_2d._images = (() => {
                    let floors = this.floors;
                    let imgs = {}, imgEl, content;
                    for (var i = 0; i < floors.length; i++) {
                        const imgEl = this._createImageObj();
                        imgEl.crossOrigin = 'anonymous';
                        content = document.createElement('div');
                        content.classList.add('img-loader');
                        content.classList.add('img-loads');
                        content.appendChild(imgEl);
                        this._setClass(content, 'content-floorplans_2d-plan');
                        imgEl.addEventListener('load', function (content, image, resize) {
                            image.style.width = 'auto';
                            image.style.height = 'auto';
                            content.dataset.width = image.width || image.clientWidth;
                            content.dataset.height = image.height || image.clientHeight;
                            content.dataset.aspect = content.dataset.width / content.dataset.height;
                            content.classList.remove('img-loads');
                            resize();
                            image.style.width = '';
                            image.style.height = '';
                        }.bind(null, content, imgEl, this._contentData.floorplans_2d.resize));

                        let src = this.metrical ? floors[i].image_url : floors[i].image_url_imperial || floors[i].image_url;

                        if(this._permissions['dimensions'] === false && floors[i].without_measurement){
                            src = floors[i].without_measurement;
                        }

                        imgEl.src = src;
                        imgs[floors[i].id] = content;
                    }
                    return imgs;
                })());
            },
            configurable: true
        })
    }

    closeContent() {
        this._closeContent();
    }

    addGyroControl() {
        if (!this._controlButtons['gyro']) this._addSimpleControl('gyro', 'gyro control-mobile', PannellumPanel.POSITION.CONTAINER, 2, false, false, 'TOOLTIP_GYRO');
    }

    addScenes(images, hideThumbnails = false) {
        const scenes = this._scenes;
        const editable = this._editable;
        const iconsList = this._iconsList.bind(this);
        const isThumbnail = scenes.find(({hide_thumbnail}) => hide_thumbnail === null);
        const narrationScenes = this._narrationScenes;

        if (!hideThumbnails && !(!isThumbnail && !editable)) {
            this._addSimpleControl('scenes', 'scenes', PannellumPanel.POSITION.CONTAINER, 0, true, false, 'TOOLTIP_SCENES');
        }
        this._contentData.scenes = {
            init: this._scenesContentInit.bind(this, images),
            destroy: this._scenesContentDestroy.bind(this),
            onresize: function () {
                this._centerSelectedScene();
            }.bind(this),
            onclick: function (el, e) {
                if(this._editable){
                    if(e.detail === 1){
                        this.debounceThumbnailClick =  setTimeout(() => {
                            if (el.classList.contains('active')) return;
                            this._triggerEvent('scenes.select', el.dataset.sceneId);
                        }, 200)
                    } else if(e.detail === 2){
                        if(this.debounceThumbnailClick)  clearTimeout(this.debounceThumbnailClick);
                        this._triggerEvent('rename.thumbnail', el.dataset.sceneId);
                    }

                    return ;
                }

                if (el.classList.contains('active')) return;

                this._triggerEvent('scenes.select', el.dataset.sceneId);
            }.bind(this),
            imageData: images,
            narrationScenes: this._narrationScenes
        };
        Object.defineProperty(this._contentData.scenes, 'images', {
            get: function () {
                return this._images || (this._images = (function () {
                    var elems = [];

                    for (var i = 0; i < images.length; i++) {
                        // A hidden thumbnail gets no element and no image request in the
                        // public viewer. The admin viewer keeps it — that is the only place
                        // the hide toggle can be flipped back off.
                        if (!editable && scenes.find(s => s.id === images[i].id)?.hide_thumbnail === '1') continue;

                        (function (img) {
                            var elem = document.createElement('li');
                            elem.classList.add('img-loader');
                            elem.classList.add('img-loads');
                            elem.dataset.sceneId = img.id;
                            elem.dataset.title = img.title;
                            elem.dataset.name = img.name;
                            // if(narrationScenes.includes(img.id)) {
                            //     elem.classList.add('narration-thumbnail');
                            // }
                           
                            var image = new Image();
                            image.crossOrigin = 'anonymous';
                            image.addEventListener('load', function () {
                                elem.classList.remove('img-loads');
                            });
                            image.src = img.thumbnail;
                            
                            elem.appendChild(iconsList(img.id));
                            elem.appendChild(image);
                            elems.push(elem);
                        })(images[i]);
                    }
                    return elems;
                })());
            }
        });
    }

    _iconsList(imgId) {
        const scenes = this._scenes;
        const narrationScenes = this._narrationScenes;
        const {hide_thumbnail} = scenes.find(({id}) => id === imgId);
        const icons =  [];
        const editable = this._editable;

        if (narrationScenes.includes(imgId)) icons.push({src: 'speaking'});
        if (editable) {
            icons.push({src: 'hide', options: {
                classList: [hide_thumbnail == 1 ? 'active' : ''],
                onclick:  (e) => {
                    let active = !e.target.classList.contains('active');
                    e.target.classList[active ? 'add' : 'remove']('active');
                    
                    this._triggerEvent('thumbnail.hide', {sceneId: imgId, checked: active});
                }
            }});
        }

        return this.createIcon(icons);
    }

    createIcon (icons) {
        const els = document.createElement('div');
        els.classList.add('thumbnail--icon');

        icons.forEach(({src, options}) => {
            const span = document.createElement('span');
            span.classList.add(`thumbnail--${src}`);

            if (options) {
                const {onclick, dataset, classList} = options;

                if (classList.filter((str) => str.length > 0).length) {
                    span.classList.add(classList.join(','));
                }
                if (onclick) span.onclick = onclick;
                if (dataset) {
                    for(let i in dataset) {
                        span.dataset[i] = dataset[i];
                    }
                }
            }

            els.appendChild(span);
        })

        return els;
    }

    checkbox(classTitle, checked, dataset = {}) {
        const el = document.createElement('input');
        el.type = 'checkbox';
        el.checked = checked;
        el.classList.add(classTitle);
        for(let i in dataset) {
            el.dataset[i] = dataset[i];
        }
        el.addEventListener("click", (e) => {
            const {sceneId} = e.target.dataset;

            this._triggerEvent('thumbnail.hide', {sceneId, checked: e.target.checked});
        });

        return el;
    }

    setScene(sceneId) {
        this._sceneId = sceneId;
        var elems = this._contentData.scenes.imageData;
        this._title.scene = elems.filter(function (el) {
            return el.id == sceneId;
        })[0]?.title || "";
        this._updateTitle();

        if (this._panelMode == 'scenes') {
            this._setActiveThumbnail(sceneId);
            this._centerSelectedScene();
        }
    }

    activateControl(name, on) {
        this._controlButtons[name].button.classList[on ? 'add' : 'remove']('active');
    }

    enableControl(name) {
        if (this._controlButtons[name])
            this._controlButtons[name].button.disabled = false;
    }

    addClass(className) {
        if (!this._panel || !className) {
            return;
        }
        this._panel.classList.add(className);
    }

    disableControl(name) {
        if (this._controlButtons[name])
            this._controlButtons[name].button.disabled = true;
    }

    on(eventName, callback) {
        if (!callback) {
            return;
        }
        this._events[eventName] = this._events[eventName] || [];
        this._events[eventName].push(callback);
    }

    off(eventName, callback) {
        if (!callback) {
            this._events[eventName] = null;
            return;
        }
        var events = this._events[eventName], index;
        if (events && events.length) {
            index = events.indexOf(callback);
            if (index > -1) {
                events.splice(index, 1);
            }
        }
    }

    destroy() {
        this._removeScenes();
        this._removeFloorplans();
        this._removeBasicControls();
        this._tooltip.destroy();

        if (this._drawer) {
            this._drawer.destroy();
            this._drawer = null;
        }

        this._destroyControls();
        this._destroyContent();
        this._destroyPanel();
        delete window.e3touchDeviceCallback;
    }

    _initPanel() {
        this._panel = document.createElement('div');
        this._setClass(this._panel);
        this._pannellum.appendChild(this._panel);
        this._e3live = document.createElement('div');
    }

    _initContent(title) {
        this._title.tour = title;
        this._content = document.createElement('div');
        this._setClass(this._content, 'content');
        this._updateTitle();
        this._panel.appendChild(this._content);
        this._closePanel();
    }

    _openPanel() {
        this._panel.classList.add('open-content');
        this._content.classList.add('open');
        this._e3live.classList.add('open');
    }

    closePanelFull() {
        this._closePanelFull();
    }

    _closePanelFull() {
        this._resetControlsButtonsClassNames();
        this._closeContent();
        this._closePanel();
    }

    _closePanel() {
        this._panel.classList.remove('open-content');
        this._content.classList.remove('open');
        this._e3live.classList.remove('open');
    }

    _toggleControll(name, action) {
        const button = this._controlButtons[name];
        if(!button) return false;

        const controller = this._controlButtons[name].button;

        controller.classList[action]("hidden");

        this._updateMenuButton();
    }

    _updateTitle() {
        this._content.dataset.title = this._title.scene || '';
    }

    _addSimpleControl(name, className, position, index, contentable, durable, title = '', dataset, align) {
        var button = document.createElement('button');
        button.className = 'control control-' + className;
        button.dataset.index = index;
        
        if(dataset) {
            for(let i in dataset) {
                button.dataset[i] = dataset[i];
            }
        }
        if (contentable) {
            button.className += ' control-contentable';
        }
        var callbacks = [];
        if (contentable) {
            callbacks.push([button, 'click', (e) => this._openContent(name, e.currentTarget), true]);
        } else {
            if (!durable) {
                callbacks.push([button, 'click', this._triggerEvent.bind(this, name), true]);
            } else {
                callbacks.push(
                    [button, 'mousedown', this._triggerEvent.bind(this, name + '-start'), true],
                    [document, 'mouseup', this._triggerEvent.bind(this, name + '-end'), true],
                    [button, 'touchstart', this._triggerEvent.bind(this, name + '-start'), {passive: true}],
                    [document, 'touchend', this._triggerEvent.bind(this, name + '-end'), true]
                );
            }
        }
        callbacks.forEach(function (callback) {
            callback[0].addEventListener.apply(callback[0], callback.slice(1));
        });
        const inDrawer = this._appendByIndex(button, position, index, name);
        this._controlButtons[name] = {
            button: button,
            callbacks: callbacks,
            position: position,
            index: index,
            durable: !!durable,
            inDrawer: inDrawer
        };

        if (inDrawer) {
            this._drawer.addControl(button, title);
            this._updateMenuButton();
        }

        if (title) {
            align = align || (!index
                ? 'left'
                : index >= 100
                    ? 'right'
                    : 'center');

            this._tooltip.add(name, button, title, align);
        }
    }

    getControlButton(name){
        return this._controlButtons[name];
    }

    syncControls(obj) {
        const {open, control} = obj;
        if(open && this._panelMode === control || !open && this._panelMode !== control) return ;

        const registered = this._controlButtons[control];
        const controlButton = registered
            ? registered.button
            : this._content.parentElement.querySelector(`.control-${control}`);

        this._openContent(control, controlButton);
    }

    _openContent(type, controlButton) {
        let promise;

        this._resetControlsButtonsClassNames();

        if (this._panelMode) {
            promise = this._contentData[this._panelMode].destroy();
        }

        if (!promise) {
            promise = Promise.resolve();

            if (document.getElementsByClassName('e3-viewer-3dplan-mini').length) document.getElementsByClassName('e3-viewer-3dplan-mini')[0].classList.remove('hidden');
        }

        promise.then(() => {
            if (!this._panel) return;

            if (type === this._panelMode) {
                this._panelMode = null;
                this._triggerEvent(type + '.close');
                this._closePanel();
                return;
            }
            if (this._panelMode == null) {
                this._openPanel();
            }
            this._panelMode = type;

            if (!controlButton || !controlButton.classList) return;

            controlButton.classList.add('active');
            this._triggerEvent(type + '.init');
            this._contentData[type].init();

            if (document.getElementsByClassName('e3-viewer-3dplan-mini').length) document.getElementsByClassName('e3-viewer-3dplan-mini')[0].classList.add('hidden');
        })
    }

    _scenesContentInit(images) {
        var wrapper = document.createElement('div');
        this._setClass(wrapper, 'content-scenes-wrapper');
        var elem, list = document.createElement('ul');
        wrapper.appendChild(list);
        this._content.appendChild(wrapper);
        this._setClass(list, 'content-scenes');
        // Not images.length — hidden scenes have no element, so the two arrays differ.
        var elems = this._contentData.scenes.images;
        for (var i = 0; i < elems.length; i++) {
            elem = elems[i];
            list.appendChild(elem);
        }
        E3Swipe.init(list, this._contentData.scenes.onclick, this._contentData.scenes.onDoublclick);
        list.classList.add('swiping');
        this._setActiveThumbnail(this._sceneId);
        this._centerSelectedScene(true);
        setTimeout(function () {
            list.classList.remove('swiping');
        }, 50);
        window.addEventListener('resize', this._contentData.scenes.onresize);

        elem = null;
    }

    _scenesContentDestroy() {
        if (this._panelMode === 'scenes') {
            var list = this._content.querySelector('ul');
            window.removeEventListener('resize', this._contentData.scenes.onresize);
            E3Swipe.destroy(list);
            this._removeChildren(this._content);
        }
    }

    getState() {
        return this._floorplan.state;
    }

    _setClass(elem, className) {
        elem.classList.add(this._classPrefix + (className ? ('-' + className) : ''));
    }

    _removeClass(elem, className) {
        elem.classList.remove(this._classPrefix + (className ? ('-' + className) : ''));
    }

    _insertBefore(newNode, referenceNode) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode);
    }

    _triggerEvent(eventName) {
        if (!this._events[eventName]) {
            return;
        }

        if (this._events[onBeforeEvent]) {
            for (let i = 0; i < this._events[onBeforeEvent].length; i++) {
                try {
                    this._events[onBeforeEvent][i].apply(null, Array.prototype.slice.call(arguments, 1));
                } catch (e) {
                    console.info(`${onBeforeEvent} is error reason`);
                    console.error(e);
                }
            }
        }

        for (let i = 0; i < this._events[eventName].length; i++) {
            this._events[eventName][i].apply(null, Array.prototype.slice.call(arguments, 1));
        }
    }

    _appendByIndex(control, position, index, name) {
        var cur, i,
            defaultMax = Number.POSITIVE_INFINITY,
            max = {index: defaultMax};

        const inDrawer = this._isDrawerControl(position, name);

        for (i in this._controlButtons) {
            cur = this._controlButtons[i];
            if (cur && cur.position == position && !!cur.inDrawer === inDrawer) {
                if (cur.index > index) {
                    max = max.index < cur.index ? max : cur;
                }
            }
        }
        if (max.index === defaultMax) {
            (inDrawer ? this._drawer.list : this._controls[position]).appendChild(control);
        } else {
            this._insertBefore(control, max.button);
        }

        return inDrawer;
    }

    _isDrawerControl(position, name) {
        return !!this._drawer
            && position === PannellumPanel.POSITION.CONTAINER
            && PannellumPanel.BAR_CONTROLS.indexOf(name) === -1;
    }

    _centerSelectedScene(force) {
        if (this._panelMode == 'scenes' || force) {
            var parent = this._content.querySelector('ul');
            var active = parent.querySelector('li.active');
            if (!active) {
                active = this._setActiveThumbnail(this._sceneId);
            }
            // The current scene's own thumbnail may be hidden, so it has no element to
            // centre on — leave the strip where it is rather than throwing in centerChild.
            if (!active) return;
            E3Swipe.centerChild(parent, active);
        }
    }

    _setActiveThumbnail(sceneId) {
        var active, elems = this._contentData.scenes.images;
        for (var i = 0; i < elems.length; i++) {
            if (!active && elems[i].dataset.sceneId == sceneId) {
                active = elems[i];
                active.classList.add('active');
            } else {
                elems[i].classList.remove('active');
            }
        }
        return active;
    }

    _closeContent() {
        if (this._panelMode) {
            this._openContent(this._panelMode);
        }
    }

    _destroyPanel() {
        this._pannellum.removeChild(this._panel);
        this._panel = null;
    }

    _destroyContent() {
        this._title = null;
        this._panel.removeChild(this._content);
        this._content = null;
    }

    _destroyControls() {
        for (var i in this._controls) {
           try{
               if (this._controls.hasOwnProperty(i) && !['container', 'left'].includes(i) && this._controls[i] && this._controls.container) {
                   this._controls.container.removeChild(this._controls[i])
                   this._controls[i] = null;
               }
               if(this._panel && this._controls[i] && this._panel.contains(this._controls[i])){
                   this._panel.removeChild(this._controls[i]);
               }
               this._controls[i] = null;
           }catch (e){
               console.log(e, i)
           }
        }

        this._controls = null;
    }

    _removeBasicControls() {
        this._removeSimpleControl('menu');
        this._removeSimpleControl('share');
        this._removeSimpleControl('fullscreen');
        this._removeSimpleControl('measurement');
        this._removeSimpleControl('vr');
        this._removeSimpleControl('gyro');
    }

    _removeFloorplans() {
        this._closeContent();
        this._removeSimpleControl('floorplans_2d');
        this._removeSimpleControl('floorplans_3d');
    }

    _removeScenes() {
        this._closeContent();
        this._removeSimpleControl('scenes');
    }

    _removeSimpleControl(name) {
        var obj = this._controlButtons[name];
        if (!obj) {
            return;
        }
        if (obj.inDrawer && this._drawer) this._drawer.removeControl(obj.button);

        this._removeByIndex(obj.button, obj.position);
        obj.callbacks.forEach(function (callback) {
            callback[0].removeEventListener.apply(callback[0], callback.slice(1));
        });
        this._controlButtons[name] = obj = null;

        this._tooltip.remove(name);
        this._updateMenuButton();
    }

    // Parent-agnostic: a control lives either in a bar group or in the drawer.
    _removeByIndex(control, position) {
        if (control && control.parentNode) control.parentNode.removeChild(control);
    }

    _touchDeviceCallback(touchDevice) {
        if (this._panel) {
            this._panel.classList[touchDevice ? 'add' : 'remove']('nohover');
        }
    }

    _addE3Live() {
        this._e3live.setAttribute('id', 'e3live')
        this._setClass(this._e3live, 'e3live');
        this._pannellum.appendChild(this._e3live);
    }

    _removeChildren(el) {
        for (var i = el.childNodes.length - 1; i >= 0; i--) {
            el.removeChild(el.childNodes[i]);
        }
    }

    _findActiveFloorplan(markers, sceneId) {
        var i;
        if (!sceneId) {
            return null;
        }
        for (i = 0; i < markers.length; i++) {
            if (markers[i].imageID == sceneId) {
                return markers[i].floorplanID;
            }
        }
        return null;
    }

    _isIOS() {
        var iDevices = [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
        ];
        if (!!navigator.platform) {
            while (iDevices.length) {
                if (navigator.platform === iDevices.pop()) {
                    return true;
                }
            }
        }
        return false;
    }

    _isMobile() {
        if (navigator.userAgent.match(/Android/i)
            || navigator.userAgent.match(/webOS/i)
            || navigator.userAgent.match(/iPhone/i)
            || navigator.userAgent.match(/iPad/i)
            || navigator.userAgent.match(/iPod/i)
            || navigator.userAgent.match(/BlackBerry/i)
            || navigator.userAgent.match(/Windows Phone/i)) return true;

        return false;
    }

    _inIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    _webglSupported() {
        try {
            var canvas = document.createElement('canvas');
            var ctx = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            ctx.getSupportedExtensions();

            if (!this._supportExtension(ctx, 'OES_standard_derivatives')) {
                // if(!this._supportExtension(ctx, 'OES_standard_derivatives' ) || !this._supportExtension(ctx, 'ANGLE_instanced_arrays' )){
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    }

    _supportExtension(gl, name) {

        var extension;

        switch (name) {

            case 'WEBGL_depth_texture':
                extension = gl.getExtension('WEBGL_depth_texture') || gl.getExtension('MOZ_WEBGL_depth_texture') || gl.getExtension('WEBKIT_WEBGL_depth_texture');
                break;

            case 'EXT_texture_filter_anisotropic':
                extension = gl.getExtension('EXT_texture_filter_anisotropic') || gl.getExtension('MOZ_EXT_texture_filter_anisotropic') || gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
                break;

            case 'WEBGL_compressed_texture_s3tc':
                extension = gl.getExtension('WEBGL_compressed_texture_s3tc') || gl.getExtension('MOZ_WEBGL_compressed_texture_s3tc') || gl.getExtension('WEBKIT_WEBGL_compressed_texture_s3tc');
                break;

            case 'WEBGL_compressed_texture_pvrtc':
                extension = gl.getExtension('WEBGL_compressed_texture_pvrtc') || gl.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc');
                break;

            case 'WEBGL_compressed_texture_etc1':
                extension = gl.getExtension('WEBGL_compressed_texture_etc1');
                break;

            default:
                extension = gl.getExtension(name);

        }

        if (extension === null) {
            console.warn('THREE.WebGLRenderer: ' + name + ' extension not supported.');
            return null
        }

        return extension;

    }

}
